/**
 * MyObjects — pure helpers for navigation-state driven refreshes.
 *
 * Extracted from MyObjects.tsx so the logic can be unit-tested in
 * isolation (same pattern as the processing-poll helper).
 */

export interface HandleClearedItemNavStateArgs {
  clearedItemId: string | undefined;
  refetch: () => void;
  /** Present for symmetry with the delete flow — but NOT called for clear. */
  removeItems: (ids: string[]) => void;
}

/**
 * Triggered when ReviewTab navigates to /my-objects after a successful
 * "Clear for Reuse". We need the list to reflect the cleared state
 * without the user having to hard-refresh.
 *
 * Strategy:
 *   1. Refetch immediately (fast path if OpenSearch already reindexed)
 *   2. Refetch again after ~2s (covers the DynamoDB Stream → indexer lag)
 *
 * We DO NOT call removeItems here: unlike delete, a cleared item still
 * belongs in the list (retired items are always shown), just with its
 * empty/CLEARED presentation.
 *
 * Returns a cleanup function that cancels the pending delayed refetch
 * (callers should invoke it on unmount or when navigation state changes).
 */
export function handleClearedItemNavState({
  clearedItemId,
  refetch,
}: HandleClearedItemNavStateArgs): (() => void) | undefined {
  if (!clearedItemId) return undefined;

  refetch();
  const timerId = setTimeout(() => refetch(), 2000);
  return () => clearTimeout(timerId);
}
